// Logs tab for a run: live tail of jobs/{jobId}/blockLogs (the runner's
// ring per block), search across blocks, and a plain .txt download of the
// full log (pipelinesApi GET /runs/:jobId/logs/:blockId/url).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCollection } from '../../data/firestore';
import { pipelineApi } from './api';

const mono = { fontFamily: 'ui-monospace, monospace', fontSize: 11.5 };
const ms = (n) => (n < 1000 ? `${n}ms` : n < 60000 ? `${(n / 1000).toFixed(1)}s` : `${Math.floor(n / 60000)}m${String(Math.floor((n % 60000) / 1000)).padStart(2, '0')}`);

export default function RunLogsPanel({ jobId, orgId, workspaceId, pipelineId, blocks, selectedBlock, onSelectBlock, active }) {
  const { docs } = useCollection(jobId ? `jobs/${jobId}/blockLogs` : 'jobs/none/blockLogs');
  const [q, setQ] = useState('');
  const [follow, setFollow] = useState(true);
  const [dl, setDl] = useState('');
  const bodyRef = useRef(null);
  const api = pipelineApi(orgId, workspaceId, pipelineId);
  const byBlock = useMemo(() => Object.fromEntries((docs || []).map((d) => [d.id, d])), [docs]);
  const order = useMemo(() => (blocks || []).map((b) => b.id).filter((id) => byBlock[id]).concat((docs || []).map((d) => d.id).filter((id) => !(blocks || []).some((b) => b.id === id))), [blocks, byBlock, docs]);
  const shown = useMemo(() => (selectedBlock && byBlock[selectedBlock] ? [selectedBlock] : order), [selectedBlock, byBlock, order]);

  const lines = useMemo(() => {
    const out = [];
    for (const id of shown) {
      const d = byBlock[id];
      for (const raw of d?.lines || []) {
        const i = raw.indexOf('|');
        const t = i > 0 ? Number(raw.slice(0, i)) : 0;
        const text = i > 0 ? raw.slice(i + 1) : raw;
        if (q && !text.toLowerCase().includes(q.toLowerCase())) continue;
        out.push({ id, label: d.label || id, t, text });
      }
    }
    return shown.length > 1 ? out.sort((a, b) => a.t - b.t) : out;
  }, [shown, byBlock, q]);

  useEffect(() => {
    if (follow && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [lines, follow]);

  const download = async (blockId) => {
    setDl('…');
    try {
      const r = await api.get(`/runs/${jobId}/logs/${blockId}/url`);
      if (r.url) { window.open(r.url, '_blank', 'noopener'); setDl(''); return; }
      const blob = new Blob([r.text || ''], { type: 'text/plain' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = `${pipelineId}-${blockId}.txt`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      setDl('');
    } catch (e) { setDl(e.message); }
  };

  if (!jobId) return <p className="cs-help" style={{ fontSize: 12, margin: 0 }}>Run the workflow to see its logs here — one stream per block, readable as it happens.</p>;
  return (
    <div data-nova-note={`Logs for run ${jobId}: ${order.length} block stream(s)${selectedBlock ? `, filtered to ${selectedBlock}` : ''}${q ? `, searching "${q}"` : ''}.`}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="cs-input" style={{ padding: '2px 6px', fontSize: 12, maxWidth: 200 }} value={selectedBlock && byBlock[selectedBlock] ? selectedBlock : ''} onChange={(e) => onSelectBlock?.(e.target.value || null)} aria-label="block">
          <option value="">All blocks (merged by time)</option>
          {order.map((id) => <option key={id} value={id}>{byBlock[id]?.label || id} · {byBlock[id]?.count || 0}</option>)}
        </select>
        <input className="cs-input" style={{ flex: 1, minWidth: 120, padding: '2px 6px', fontSize: 12 }} placeholder="search…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="search logs" />
        <label className="cs-help" style={{ fontSize: 11.5, display: 'flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={follow} onChange={(e) => setFollow(e.target.checked)} /> follow
        </label>
        {shown.length === 1 && (
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => download(shown[0])} title="Full log as plain text (.txt, not .gz)">
            ⤓ .txt{byBlock[shown[0]]?.bytes ? ` · ${Math.round(byBlock[shown[0]].bytes / 1024)} KB` : ''}
          </button>
        )}
      </div>
      {dl && dl !== '…' && <p className="cs-help" style={{ fontSize: 12, color: 'var(--danger, #e5484d)', margin: '4px 0 0' }}>{dl}</p>}
      <div ref={bodyRef} className="nq-logs" onScroll={(e) => { const el = e.currentTarget; setFollow(el.scrollHeight - el.scrollTop - el.clientHeight < 24); }}>
        {lines.length === 0 && <span className="cs-help" style={{ fontSize: 12 }}>{active ? 'Waiting for output…' : q ? 'No lines match.' : 'No log lines were written for this run.'}</span>}
        {lines.map((l, i) => (
          <div key={i} className="nq-logline" style={mono}>
            <span className="nq-logt">{ms(l.t)}</span>
            {shown.length > 1 && <span className="nq-logb" title={l.id}>{l.label}</span>}
            <span className={/error|traceback|exception|failed/i.test(l.text) ? 'nq-logerr' : ''}>{l.text}</span>
          </div>
        ))}
      </div>
      {shown.length === 1 && (byBlock[shown[0]]?.count || 0) > (byBlock[shown[0]]?.lines || []).length && (
        <p className="cs-help" style={{ fontSize: 11.5, margin: '4px 0 0' }}>Showing the last {(byBlock[shown[0]].lines || []).length} of {byBlock[shown[0]].count} lines — download the .txt for everything.</p>
      )}
    </div>
  );
}
